import React, { useEffect, useState } from "react";
import s from "./DuelRoundWinLab.module.scss";
import DuelFightCard from "../play/round-over/DuelFightCard";

// /debug/duel-round-win: designs for who took the round in a ranked 1v1
// (first to 3), and further down, in a ranked 6 FFA (first to 30). Each plays on a mock arena at desktop size and at phone size
// (everything inside sizes in container units, so one component serves both).
// The bar picks the score going in and the winner, so the special beats show:
// match point, the decider (2-2) and the match win. Linked from the Admin page.

const YOU = { name: "You", color: "#03A9F4" };
const THEM = { name: "Kurvenkönig", color: "#E91E63" };
const GOLD = "#ffcf4d";

const SCORES: { label: string; you: number; them: number }[] = [
  { label: "0-0", you: 0, them: 0 },
  { label: "1-1", you: 1, them: 1 },
  { label: "2-1", you: 2, them: 1 },
  { label: "2-2", you: 2, them: 2 },
];

const DESIGNS = [
  { id: "LIVE", name: "Fight card (the live component)", blurb: "Design E as it now plays in ranked 1v1 matches (round-over/DuelFightCard), over the mock board." },
  { id: "A", name: "Tug of war", blurb: "A rope across the arena's top, split in the two colours. The round shoves the knot a notch into the loser's end, so the winner's colour takes more rope." },
  { id: "B", name: "Versus slabs", blurb: "The match-found banner comes back: both slabs slam in, the loser's is knocked out, the winner's widens with the score in the VS block." },
  { id: "C", name: "Point flies home", blurb: "A spark leaves the winner's head and arcs into their mark in the HUD's First to 3 row, which lights with a ring. Quiet, and it teaches the score." },
  { id: "D", name: "Colour flood", blurb: "The winner's colour floods out from their head across the arena, the loser's trail goes grey. Name huge in the middle, digits flip." },
  { id: "E", name: "Fight card", blurb: "Arcade style: the board dims, the score slams in big, the winner's digit knocks up with a shake. Match point and decider get a gold stamp." },
  { id: "E2", name: "Fight card: split panels", blurb: "The screen splits on a slant into the two colours, a big digit on each side. The winner's panel shoves the slant over into the loser's half." },
  { id: "E3", name: "Fight card: flip clock", blurb: "Two scoreboard cards; the winner's flips over to its new number, names under them, a gold ribbon on the big rounds." },
  { id: "E4", name: "Fight card: speed lines", blurb: "Manga burst: speed lines in the winner's colour rush in, the name slams on a slant with +1, the score small underneath." },
  { id: "E5", name: "Fight card: neon", blurb: "The score as a neon sign: the tubes buzz on, the winner's old number flickers out and the new one stutters to life." },
  { id: "E6", name: "Fight card: impact frame", blurb: "Hit-stop: a white flash, the score splits into red and cyan and snaps back together, a shockwave rolls out from the winner's digit." },
];

type Side = "you" | "them";
interface Beat {
  winner: typeof YOU;
  loser: typeof YOU;
  side: Side;
  before: { you: number; them: number };
  after: { you: number; them: number };
  round: number;
  tag: string;        // what this round means
  special: boolean;   // match point, decider or the match itself
}

function beatFor(score: { you: number; them: number }, side: Side): Beat {
  const after = { ...score, [side]: score[side] + 1 };
  const w = after[side];
  const l = side === "you" ? after.them : after.you;
  const round = score.you + score.them + 1;
  let tag = `Takes round ${round}`;
  if (w === 3) tag = "Wins the match";
  else if (w === 2 && l === 2) tag = "Decider next";
  else if (w === 2) tag = "Match point";
  return {
    winner: side === "you" ? YOU : THEM,
    loser: side === "you" ? THEM : YOU,
    side, before: score, after, round, tag,
    special: w === 3 || w === 2,
  };
}

// The mock board, in a 100 x 100 box: the winner's trail runs on to its head,
// the loser's ends in a crash. The heads' spots are shared with the designs.
const HEAD = { x: 70, y: 30 };
const CRASH = { x: 38, y: 55 };
function Board({ beat, greyLoser }: { beat: Beat; greyLoser?: boolean }) {
  return (
    <svg className={s.board} viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
      <path d="M14 84 C 28 44, 50 76, 58 50 S 78 18, 70 30" stroke={beat.winner.color} className={s.trail} />
      <path d="M86 88 C 72 76, 60 94, 48 80 S 30 64, 38 55" stroke={beat.loser.color} className={`${s.trail} ${greyLoser ? s.trailGrey : ""}`} />
      <circle cx={HEAD.x} cy={HEAD.y} r="2.2" fill="#ffb74d" />
      <circle cx={CRASH.x} cy={CRASH.y} r="2.2" fill="#ffb74d" opacity="0.55" />
    </svg>
  );
}

// The First to 3 marks (the duel HUD's pips): one per round played, in its
// winner's colour. `lit` is how many of the winner's rounds show lit.
function Marks({ beat, landing }: { beat: Beat; landing?: boolean }) {
  // rounds so far alternate from the score going in, then this round
  const seq: string[] = [];
  for (let i = 0; i < beat.before.you; i++) seq.push(YOU.color);
  for (let i = 0; i < beat.before.them; i++) seq.push(THEM.color);
  return (
    <span className={s.marks}>
      {[0, 1, 2, 3, 4].map((i) => {
        const past = i < seq.length;
        const now = i === seq.length;
        return (
          <i key={i}
            className={`${past ? s.markOn : ""} ${now && landing ? s.markLand : ""}`}
            style={past ? ({ "--dot": seq[i] } as React.CSSProperties) : now ? ({ "--dot": beat.winner.color } as React.CSSProperties) : undefined} />
        );
      })}
    </span>
  );
}

// A score digit that ticks from its old value to its new one (flip).
function Digit({ from, to, className }: { from: number; to: number; className?: string }) {
  if (from === to) return <span className={`${s.digit} ${className || ""}`}>{to}</span>;
  return (
    <span className={`${s.digit} ${s.digitFlip} ${className || ""}`}>
      <span className={s.digitOld}>{from}</span>
      <span className={s.digitNew}>{to}</span>
    </span>
  );
}

function Score({ beat, className }: { beat: Beat; className?: string }) {
  return (
    <span className={`${s.score} ${className || ""}`}>
      <Digit from={beat.before.you} to={beat.after.you} className={beat.side === "you" ? s.digitWin : ""} />
      <span className={s.scoreSep}>:</span>
      <Digit from={beat.before.them} to={beat.after.them} className={beat.side === "them" ? s.digitWin : ""} />
    </span>
  );
}

function DesignA({ beat }: { beat: Beat }) {
  // the knot: 50% at level, pushed a notch (15%) per round of lead into the
  // trailing player's end, so the leader's colour takes more of the rope
  const pos = (sc: { you: number; them: number }) => 50 + (sc.you - sc.them) * 15;
  return (
    <>
      <Board beat={beat} />
      <div className={s.aWrap}>
        <div className={s.aRope} style={{ "--from": `${pos(beat.before)}%`, "--to": `${pos(beat.after)}%`, "--you": YOU.color, "--them": THEM.color } as React.CSSProperties}>
          <span className={s.aYou} />
          <span className={s.aThem} />
          {[20, 35, 65, 80].map((n) => <i key={n} className={s.aNotch} style={{ left: `${n}%` }} />)}
          <span className={s.aKnot} />
        </div>
        <div className={s.aEnds}>
          <span style={{ color: YOU.color }}><Digit from={beat.before.you} to={beat.after.you} className={beat.side === "you" ? s.digitWin : ""} /></span>
          <span style={{ color: THEM.color }}><Digit from={beat.before.them} to={beat.after.them} className={beat.side === "them" ? s.digitWin : ""} /></span>
        </div>
        <div className={s.aCaption}>
          <b style={{ color: beat.winner.color }}>{beat.winner.name}</b>
          <span className={beat.special ? s.gold : ""}>{beat.tag}</span>
        </div>
      </div>
    </>
  );
}

function DesignB({ beat }: { beat: Beat }) {
  const youWon = beat.side === "you";
  return (
    <>
      <Board beat={beat} />
      <div className={s.bDim} />
      <div className={s.bBand}>
        <div className={`${s.bSlab} ${s.bLeft} ${youWon ? s.bWin : s.bLose}`} style={{ "--c": YOU.color } as React.CSSProperties}>
          <span className={s.bName}>{YOU.name}</span>
        </div>
        <div className={s.bVs}><Score beat={beat} /></div>
        <div className={`${s.bSlab} ${s.bRight} ${youWon ? s.bLose : s.bWin}`} style={{ "--c": THEM.color } as React.CSSProperties}>
          <span className={s.bName}>{THEM.name}</span>
        </div>
      </div>
      <div className={`${s.bTag} ${beat.special ? s.gold : ""}`} style={{ "--c": beat.winner.color } as React.CSSProperties}>
        <b>{beat.winner.name}</b> · {beat.tag}
      </div>
    </>
  );
}

function DesignC({ beat }: { beat: Beat }) {
  // the spark flies from the head to the mark's slot in the HUD row above
  const slot = beat.before.you + beat.before.them;
  const x1 = 57 + slot * 7.2; // the marks row's slots, in % of the frame
  return (
    <>
      <div className={s.cHud}>
        <span className={s.cLabel}>Round {beat.round}</span>
        <span className={s.cLabel}>First to 3</span>
        <Marks beat={beat} landing />
      </div>
      <Board beat={beat} />
      <span className={s.cPulse} style={{ left: `${HEAD.x}%`, top: `${HEAD.y}%`, "--c": beat.winner.color } as React.CSSProperties} />
      {/* outer runs the x, inner the y (eased differently): an arc */}
      <span className={s.cFlyX} style={{ "--x0": `${HEAD.x}%`, "--x1": `${x1}%` } as React.CSSProperties}>
        <span className={s.cFlyY} style={{ "--y0": `${HEAD.y}%`, "--c": beat.winner.color } as React.CSSProperties} />
      </span>
      <div className={s.cToast} style={{ "--c": beat.winner.color } as React.CSSProperties}>
        <b>+1</b> {beat.winner.name} <span className={beat.special ? s.gold : ""}>{beat.special ? `· ${beat.tag}` : ""}</span>
      </div>
    </>
  );
}

function DesignD({ beat }: { beat: Beat }) {
  return (
    <>
      <Board beat={beat} greyLoser />
      <div className={s.dFlood} style={{ "--c": beat.winner.color, "--hx": `${HEAD.x}%`, "--hy": `${HEAD.y}%` } as React.CSSProperties} />
      <div className={s.dCenter}>
        <div className={s.dName} style={{ color: beat.winner.color }}>{beat.winner.name}</div>
        <div className={`${s.dTag} ${beat.special ? s.gold : ""}`}>{beat.tag}</div>
        <Score beat={beat} className={s.dScore} />
      </div>
    </>
  );
}

function DesignE({ beat }: { beat: Beat }) {
  return (
    <div className={s.eShake}>
      <Board beat={beat} />
      <div className={s.eDim} />
      <div className={s.eCenter}>
        <div className={s.eRound}>Round {beat.round}</div>
        <div className={s.eScore}>
          <span style={{ color: YOU.color }}><Digit from={beat.before.you} to={beat.after.you} className={beat.side === "you" ? s.eSlam : ""} /></span>
          <span className={s.eColon}>:</span>
          <span style={{ color: THEM.color }}><Digit from={beat.before.them} to={beat.after.them} className={beat.side === "them" ? s.eSlam : ""} /></span>
        </div>
        <div className={s.eUnder} style={{ "--c": beat.winner.color } as React.CSSProperties} />
        <div className={s.eWho} style={{ color: beat.winner.color }}>{beat.winner.name}</div>
        {beat.special && <div className={s.eStamp}>{beat.tag}</div>}
      </div>
    </div>
  );
}

function DesignE2({ beat }: { beat: Beat }) {
  // the slant's top and bottom x (%): level at 55/45, shoved 12% by the round
  const shove = beat.side === "you" ? 12 : -12;
  return (
    <>
      <Board beat={beat} />
      <div className={s.e2Wrap} style={{ "--you": YOU.color, "--them": THEM.color, "--cut0": "55%", "--cut1": `${55 + shove}%` } as React.CSSProperties}>
        <div className={`${s.e2Panel} ${s.e2You}`}>
          <span className={s.e2Digit}><Digit from={beat.before.you} to={beat.after.you} /></span>
          <span className={s.e2Name}>{YOU.name}</span>
        </div>
        <div className={`${s.e2Panel} ${s.e2Them}`}>
          <span className={s.e2Digit}><Digit from={beat.before.them} to={beat.after.them} /></span>
          <span className={s.e2Name}>{THEM.name}</span>
        </div>
      </div>
      <div className={`${s.e2Tag} ${beat.special ? s.gold : ""}`}>{beat.winner.name} · {beat.tag}</div>
    </>
  );
}

function FlipCard({ from, to, color, name }: { from: number; to: number; color: string; name: string }) {
  const flips = from !== to;
  return (
    <div className={s.e3Col}>
      <div className={s.e3Card}>
        <span className={`${s.e3Face} ${flips ? s.e3Old : ""}`}>{from}</span>
        {flips && <span className={`${s.e3Face} ${s.e3New}`} style={{ color }}>{to}</span>}
        <i className={s.e3Seam} />
      </div>
      <span className={s.e3Name} style={{ color }}>{name}</span>
    </div>
  );
}
function DesignE3({ beat }: { beat: Beat }) {
  return (
    <>
      <Board beat={beat} />
      <div className={s.eDim} />
      <div className={s.e3Center}>
        <div className={s.eRound}>Round {beat.round}</div>
        <div className={s.e3Row}>
          <FlipCard from={beat.before.you} to={beat.after.you} color={YOU.color} name={YOU.name} />
          <FlipCard from={beat.before.them} to={beat.after.them} color={THEM.color} name={THEM.name} />
        </div>
        {beat.special ? <div className={s.e3Ribbon}>{beat.tag}</div> : <div className={s.e3Plain}>{beat.winner.name} · {beat.tag}</div>}
      </div>
    </>
  );
}

function DesignE4({ beat }: { beat: Beat }) {
  return (
    <>
      <Board beat={beat} />
      <div className={s.e4Lines} style={{ "--c": beat.winner.color } as React.CSSProperties} />
      <div className={s.e4Center}>
        <div className={s.e4Name} style={{ color: beat.winner.color }}>{beat.winner.name}</div>
        <div className={s.e4Plus}>+1</div>
        <div className={s.e4Under}>
          <Score beat={beat} className={s.e4Score} />
          {beat.special && <span className={s.gold}>{beat.tag}</span>}
        </div>
      </div>
    </>
  );
}

function NeonDigit({ from, to, color, changes }: { from: number; to: number; color: string; changes: boolean }) {
  return (
    <span className={s.e5Digit} style={{ "--c": color } as React.CSSProperties}>
      {changes ? (
        <>
          <span className={s.e5Old}>{from}</span>
          <span className={s.e5New}>{to}</span>
        </>
      ) : <span className={s.e5On}>{to}</span>}
    </span>
  );
}
function DesignE5({ beat }: { beat: Beat }) {
  return (
    <>
      <Board beat={beat} />
      <div className={s.e5Wall} />
      <div className={s.e5Center}>
        <div className={s.e5Sign}>
          <NeonDigit from={beat.before.you} to={beat.after.you} color={YOU.color} changes={beat.side === "you"} />
          <span className={s.e5Vs}>vs</span>
          <NeonDigit from={beat.before.them} to={beat.after.them} color={THEM.color} changes={beat.side === "them"} />
        </div>
        <div className={s.e5Who} style={{ "--c": beat.winner.color } as React.CSSProperties}>{beat.winner.name}</div>
        <div className={`${s.e5Tag} ${beat.special ? s.gold : ""}`}>{beat.tag}</div>
      </div>
    </>
  );
}

function DesignE6({ beat }: { beat: Beat }) {
  const text = `${beat.after.you} : ${beat.after.them}`;
  return (
    <div className={s.e6Stop}>
      <Board beat={beat} />
      <div className={s.eDim} />
      <div className={s.e6Center}>
        <div className={s.eRound}>Round {beat.round}</div>
        <div className={s.e6Score} data-text={text}>
          <span style={{ color: YOU.color }}>{beat.after.you}</span>
          <span className={s.eColon}> : </span>
          <span style={{ color: THEM.color }}>{beat.after.them}</span>
          <i className={s.e6Red} aria-hidden="true">{text}</i>
          <i className={s.e6Cyan} aria-hidden="true">{text}</i>
          <i className={s.e6Wave} style={{ left: beat.side === "you" ? "18%" : "82%", "--c": beat.winner.color } as React.CSSProperties} />
        </div>
        <div className={s.eWho} style={{ color: beat.winner.color }}>{beat.winner.name}</div>
        {beat.special && <div className={`${s.e6Tag} ${s.gold}`}>{beat.tag}</div>}
      </div>
      <div className={s.e6Flash} />
    </div>
  );
}

// The real component the ranked duel uses (round-over/DuelFightCard), on the
// mock board: to check the port of E against the sketch.
function DesignLive({ beat }: { beat: Beat }) {
  const side = (p: typeof YOU, id: string, colorId: string, k: Side) => ({ id, name: p.name, colorId, before: beat.before[k], after: beat.after[k] });
  return (
    <>
      <Board beat={beat} />
      <DuelFightCard left={side(YOU, "a", "blue", "you")} right={side(THEM, "b", "pink", "them")} round={beat.round} maxScore={3} totalSeconds={60} />
    </>
  );
}

const RENDER: Record<string, (p: { beat: Beat }) => React.ReactElement> = { LIVE: DesignLive, A: DesignA, B: DesignB, C: DesignC, D: DesignD, E: DesignE, E2: DesignE2, E3: DesignE3, E4: DesignE4, E5: DesignE5, E6: DesignE6 };


// ---------- 6 FFA ----------
// Six players, first to 30 points. A round pays out by survival: the first
// out gets 0, each death after pays everyone still alive, so the order out is
// 0, 1, 2, 3, 4 and the last one standing takes 5.
const FFA_PLAYERS = [
  { id: "you", name: "You", color: "#03A9F4" },
  { id: "k", name: "Kurvenkönig", color: "#E91E63" },
  { id: "g", name: "Snekmaster", color: "#8BC34A" },
  { id: "y", name: "Doctor Dash", color: "#FFC107" },
  { id: "p", name: "Houdini", color: "#9C27B0" },
  { id: "o", name: "Lord Linington", color: "#FF7043" },
];
type FfaId = string;
const FFA_SCORES: { label: string; round: number; score: Record<FfaId, number> }[] = [
  { label: "Early", round: 2, score: { you: 3, k: 5, g: 1, y: 4, p: 0, o: 2 } },
  { label: "Lead change", round: 6, score: { you: 16, k: 18, g: 11, y: 14, p: 6, o: 9 } },
  { label: "Match point", round: 8, score: { you: 21, k: 24, g: 15, y: 19, p: 10, o: 13 } },
  { label: "Match win", round: 10, score: { you: 26, k: 27, g: 19, y: 23, p: 13, o: 16 } },
];
// The order out, winner last: everyone but the winner in a fixed order.
const OUT_ORDER: FfaId[] = ["k", "p", "o", "g", "y", "you"];

interface FfaRow { id: FfaId; name: string; color: string; before: number; gain: number; after: number; rankBefore: number; rankAfter: number; outAt: number }
interface FfaBeat { rows: FfaRow[]; winner: FfaRow; round: number; tag: string; special: boolean }

function ffaBeatFor(sc: typeof FFA_SCORES[number], winnerId: FfaId): FfaBeat {
  const order = OUT_ORDER.filter((id) => id !== winnerId).concat(winnerId);
  const rows: FfaRow[] = FFA_PLAYERS.map((p) => {
    const outAt = order.indexOf(p.id);
    const before = sc.score[p.id];
    return { ...p, before, gain: outAt, after: before + outAt, rankBefore: 0, rankAfter: 0, outAt };
  });
  const rank = (key: "before" | "after") => [...rows].sort((a, b) => b[key] - a[key] || a.id.localeCompare(b.id));
  rank("before").forEach((r, i) => (r.rankBefore = i));
  rank("after").forEach((r, i) => (r.rankAfter = i));
  const winner = rows.find((r) => r.id === winnerId)!;
  const leaderBefore = rank("before")[0];
  const leaderAfter = rank("after")[0];
  let tag = `Takes round ${sc.round}`;
  if (leaderAfter.after >= 30) tag = `${leaderAfter.name} wins the match`;
  else if (leaderAfter.after >= 25 && leaderBefore.before < 25) tag = "Match point";
  else if (leaderAfter.id !== leaderBefore.id) tag = "New leader";
  return { rows, winner, round: sc.round, tag, special: tag !== `Takes round ${sc.round}` };
}

function Crown() {
  return <span className={s.crown} aria-hidden="true">👑</span>;
}

// F1: the fight card for six. Winner's name slams, +5 stamps, then the six
// scores flip in a strip with each one's gain floating up.
function FfaF1({ beat }: { beat: FfaBeat }) {
  const byBefore = [...beat.rows].sort((a, b) => a.rankBefore - b.rankBefore);
  return (
    <>
      <div className={s.eDim} />
      <div className={s.f1Center}>
        <div className={s.eRound}>Round {beat.round}</div>
        <div className={s.f1Name} style={{ color: beat.winner.color }}>{beat.winner.name}</div>
        <div className={s.f1Plus}>+{beat.winner.gain}</div>
        <div className={s.f1Strip}>
          {byBefore.map((r, i) => (
            <div key={r.id} className={s.f1Chip} style={{ "--c": r.color, animationDelay: `${0.9 + i * 0.06}s` } as React.CSSProperties}>
              <i />
              <Digit from={r.before} to={r.after} className={r.id === beat.winner.id ? s.digitWin : ""} />
              {r.gain > 0 && <b className={s.f1Gain} style={{ animationDelay: `${1.2 + i * 0.06}s` }}>+{r.gain}</b>}
            </div>
          ))}
        </div>
        {beat.special && <div className={s.eStamp}>{beat.tag}</div>}
      </div>
    </>
  );
}

// F2: race to 30. Six lanes, each bar grows by its gain toward the finish
// line; the leader wears the crown.
function FfaF2({ beat }: { beat: FfaBeat }) {
  const byAfter = [...beat.rows].sort((a, b) => a.rankAfter - b.rankAfter);
  return (
    <>
      <div className={s.f2Wrap}>
        <div className={s.f2Head}><span>Round {beat.round}</span><span className={beat.special ? s.gold : ""}>{beat.special ? beat.tag : `${beat.winner.name} +${beat.winner.gain}`}</span></div>
        {byAfter.map((r) => (
          <div key={r.id} className={`${s.f2Lane} ${r.id === beat.winner.id ? s.f2Win : ""}`}>
            <span className={s.f2Name} style={{ color: r.color }}>{r.name}</span>
            <span className={s.f2Track}>
              <span className={s.f2Bar} style={{ "--c": r.color, "--w0": `${Math.min(100, (r.before / 30) * 100)}%`, "--w1": `${Math.min(100, (r.after / 30) * 100)}%` } as React.CSSProperties}>
                {r.rankAfter === 0 && <Crown />}
              </span>
            </span>
            <span className={s.f2Pts}><Digit from={r.before} to={r.after} />{r.gain > 0 && <b style={{ color: r.color }}>+{r.gain}</b>}</span>
          </div>
        ))}
        <i className={s.f2Finish} />
      </div>
    </>
  );
}

// F3: the standings shuffle. The six rows sit in their old order, the gains
// pop, then the rows slide to their new places with up / down arrows.
function FfaF3({ beat }: { beat: FfaBeat }) {
  return (
    <>
      <div className={s.eDim} />
      <div className={s.f3Wrap}>
        <div className={s.f3Title}>Round {beat.round} <span className={beat.special ? s.gold : ""}>{beat.special ? beat.tag : `Round to ${beat.winner.name}`}</span></div>
        <div className={s.f3List}>
          {beat.rows.map((r) => {
            const moved = r.rankBefore - r.rankAfter;
            return (
              <div key={r.id} className={`${s.f3Row} ${r.id === beat.winner.id ? s.f3Win : ""}`} style={{ "--r0": r.rankBefore, "--r1": r.rankAfter, "--c": r.color } as React.CSSProperties}>
                <span className={s.f3Pos}>{r.rankAfter + 1}</span>
                <i className={s.f3Dot} />
                <span className={s.f3Name}>{r.name}{r.rankAfter === 0 && <Crown />}</span>
                {moved !== 0 && <span className={moved > 0 ? s.f3Up : s.f3Down}>{moved > 0 ? "▲" : "▼"}{Math.abs(moved)}</span>}
                <span className={s.f3Gain}>{r.gain > 0 ? `+${r.gain}` : ""}</span>
                <span className={s.f3Pts}><Digit from={r.before} to={r.after} /></span>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}

// F4: the round, replayed as its order out. Six tiles drop in one by one in
// the order they crashed, each with its points; the survivor's lands last,
// gold, with the crown.
function FfaF4({ beat }: { beat: FfaBeat }) {
  const byOut = [...beat.rows].sort((a, b) => a.outAt - b.outAt);
  return (
    <>
      <div className={s.eDim} />
      <div className={s.f4Wrap}>
        <div className={s.eRound}>Round {beat.round}</div>
        <div className={s.f4Tiles}>
          {byOut.map((r, i) => (
            <div key={r.id} className={`${s.f4Tile} ${i === 5 ? s.f4Last : ""}`} style={{ "--c": r.color, animationDelay: `${0.15 + i * 0.22}s` } as React.CSSProperties}>
              {i === 5 && <Crown />}
              <i className={s.f4Swatch} />
              <span className={s.f4Name}>{r.name}</span>
              <b className={s.f4Pts}>+{r.gain}</b>
            </div>
          ))}
        </div>
        <div className={`${s.f4Tag} ${beat.special ? s.gold : ""}`}>{beat.special ? beat.tag : `Last one standing: ${beat.winner.name}`}</div>
      </div>
    </>
  );
}

const FFA_DESIGNS = [
  { id: "F1", name: "Fight card for six", blurb: "The duel's fight card grown to six: the winner's name slams in with +5, then a strip of all six scores flips, each gain floating up.", C: FfaF1 },
  { id: "F2", name: "Race to 30", blurb: "Six lanes toward a finish line at 30; every bar surges by its gain, the leader wears the crown. Shows how close the match is at a glance.", C: FfaF2 },
  { id: "F3", name: "Standings shuffle", blurb: "The table in its old order, the gains pop, then the rows slide to their new places with up / down arrows. The most informative.", C: FfaF3 },
  { id: "F4", name: "Order out", blurb: "The round replayed as the order players crashed: tiles drop in one by one with their points, the last one standing lands gold with the crown.", C: FfaF4 },
];

function FfaSection({ run, bump }: { run: number; bump: () => void }) {
  const [idx, setIdx] = useState(1);
  const [winnerId, setWinnerId] = useState<FfaId>("you");
  const beat = ffaBeatFor(FFA_SCORES[idx], winnerId);
  useEffect(() => bump(), [idx, winnerId]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <>
      <h1 className={`${s.h1} ${s.h1Next}`}>6 FFA ranked</h1>
      <div className={s.bar}>
        <span className={s.barLabel}>Standings going in</span>
        <div className={s.seg}>
          {FFA_SCORES.map((sc, i) => <button key={sc.label} className={`${s.segBtn} ${i === idx ? s.segOn : ""}`} onClick={() => setIdx(i)}>{sc.label}</button>)}
        </div>
        <span className={s.barLabel}>Round goes to</span>
        <div className={s.seg}>
          {FFA_PLAYERS.slice(0, 3).map((p) => <button key={p.id} className={`${s.segBtn} ${winnerId === p.id ? s.segOn : ""}`} onClick={() => setWinnerId(p.id)}>{p.name}</button>)}
        </div>
        <button className={s.segBtn} onClick={bump}>Replay</button>
        <span className={s.hint}>{beat.special ? <b style={{ color: GOLD }}>{beat.tag}</b> : "an ordinary round"}</span>
      </div>
      {FFA_DESIGNS.map((d) => (
        <section key={d.id} className={s.row}>
          <h2 className={s.h2}><b>{d.id}</b> {d.name} <span>{d.blurb}</span></h2>
          <div className={s.pair}>
            <div className={`${s.frame} ${s.frameDesk} ${s.frameFfa}`} key={`desk-${run}`}><FfaBoard /><d.C beat={beat} /></div>
            <div className={`${s.frame} ${s.framePhone} ${s.frameFfa}`} key={`phone-${run}`}><FfaBoard /><d.C beat={beat} /></div>
          </div>
        </section>
      ))}
    </>
  );
}

// six trails on the big board, for the background
function FfaBoard() {
  const paths = [
    "M10 90 C 20 60, 35 80, 40 55", "M90 88 C 75 70, 85 50, 70 45", "M12 15 C 30 25, 25 40, 42 38",
    "M88 12 C 70 20, 78 35, 62 30", "M50 95 C 55 80, 45 75, 52 65", "M30 70 C 45 60, 60 70, 66 58",
  ];
  return (
    <svg className={s.board} viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
      {paths.map((d, i) => <path key={i} d={d} stroke={FFA_PLAYERS[i].color} className={s.trail} opacity="0.7" />)}
    </svg>
  );
}

export default function DuelRoundWinLab() {
  const [scoreIdx, setScoreIdx] = useState(1);
  const [side, setSide] = useState<Side>("you");
  const [loop, setLoop] = useState(true);
  const [run, setRun] = useState(0);
  const beat = beatFor(SCORES[scoreIdx], side);
  // replay on every change, and every 5s while looping
  useEffect(() => setRun((r) => r + 1), [scoreIdx, side]);
  useEffect(() => {
    if (!loop) return;
    const id = setInterval(() => setRun((r) => r + 1), 5000);
    return () => clearInterval(id);
  }, [loop]);

  return (
    <div className={s.lab}>
      <div className={s.bar}>
        <span className={s.barLabel}>Score going in</span>
        <div className={s.seg}>
          {SCORES.map((sc, i) => <button key={sc.label} className={`${s.segBtn} ${i === scoreIdx ? s.segOn : ""}`} onClick={() => setScoreIdx(i)}>{sc.label}</button>)}
        </div>
        <span className={s.barLabel}>Round goes to</span>
        <div className={s.seg}>
          <button className={`${s.segBtn} ${side === "you" ? s.segOn : ""}`} onClick={() => setSide("you")}>You</button>
          <button className={`${s.segBtn} ${side === "them" ? s.segOn : ""}`} onClick={() => setSide("them")}>Them</button>
        </div>
        <button className={s.segBtn} onClick={() => setRun((r) => r + 1)}>Replay</button>
        <label className={s.hint}><input type="checkbox" checked={loop} onChange={(e) => setLoop(e.target.checked)} /> loop every 5s</label>
        <span className={s.hint}>
          you are blue ({SCORES[scoreIdx].you}), they are pink ({SCORES[scoreIdx].them}): this round makes it {beat.after.you}-{beat.after.them}
          {beat.special ? <b style={{ color: GOLD }}> · {beat.tag}</b> : null}
        </span>
      </div>
      <h1 className={s.h1}>1 vs 1 ranked</h1>
      {DESIGNS.map((d) => {
        const Design = RENDER[d.id];
        return (
          <section key={d.id} className={s.row}>
            <h2 className={s.h2}><b>{d.id}</b> {d.name} <span>{d.blurb}</span></h2>
            <div className={s.pair}>
              <div className={`${s.frame} ${s.frameDesk} ${s["frame" + d.id]}`} key={`desk-${run}`}><Design beat={beat} /></div>
              <div className={`${s.frame} ${s.framePhone} ${s["frame" + d.id]}`} key={`phone-${run}`}><Design beat={beat} /></div>
            </div>
          </section>
        );
      })}
      <FfaSection run={run} bump={() => setRun((r) => r + 1)} />
    </div>
  );
}
